<template>
    <b-modal ref="modal" size="xl" scrollable no-close-on-backdrop no-enforce-focus :title="title" @hide="preventHideWhileSaving" @hidden="reset">
        <div class="alert alert-danger" v-show="errorMessages.length">
            <ul class="mb-0 pl-3">
                <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
            </ul>
        </div>

        <div v-show="loading" class="text-center ix-text-secondary py-5">
            <i class="far fa-spinner fa-spin"></i> Chargement…
        </div>

        <!-- Le formulaire de la page de création, compilé à chaque ouverture. Seul enfant de
             son conteneur : vue-fragment ne supporte pas le retrait d'un nœud entre deux voisins. -->
        <div ref="body" v-show="!loading">
            <component :is="form" v-if="form" :key="formKey"></component>
        </div>

        <template #modal-footer>
            <button type="button" class="ix-btn transparent" :disabled="saving" @click="$refs.modal.hide()">Annuler</button>
            <button type="button" class="ix-btn green" :disabled="loading || saving" @click="submit">
                <i class="far fa-spinner fa-spin" v-show="saving"></i> Créer l'article
            </button>
        </template>
    </b-modal>
</template>

<script>
    import $ from 'jquery';
    import ArticleFamilySelector from './ArticleFamilySelector';
    import ArticleKitComponentsTableForm from './ArticleKitComponentsTableForm';
    import ArticleKitPricingSummary from './ArticleKitPricingSummary';
    import ArticleProviderTableForm from './ArticleProviderTableForm';
    import ArticleEcoTaxForm from './ArticleEcoTaxForm';
    import ArticleAttributeForm from './ArticleAttributeForm';
    import ArticleSerialNumberForm from './ArticleSerialNumberForm';
    import EmplacementTreeSelector from './EmplacementTreeSelector';

    /**
     * Création d'un article depuis une ligne de document ou d'étude, sans quitter la page.
     * Le formulaire vient du serveur (articles.create en mode modale), pré-rempli depuis la ligne.
     *
     * @see docs/DRI/Phase2/creation-article-a-la-volee.md § 9.2
     */
    export default {
        name: 'article-quick-create-modal',
        props: {
            formRoute: { type: String, required: true },
        },
        data() {
            return {
                form: null,
                formEl: null,
                formKey: 0,
                title: 'Nouvel article',
                loading: false,
                saving: false,
                errorMessages: [],
                callback: null,
            };
        },
        methods: {
            /**
             * source : { article_id, article_name } pour une copie, ou les champs d'une saisie libre
             * (designation, reference, unit, description, amount). callback(article) à la création.
             */
            open(source, callback) {
                this.callback = callback;
                this.title = source.article_id ? 'Copie de ' + source.article_name : 'Nouvel article';
                this.errorMessages = [];
                this.loading = true;
                this.$refs.modal.show();

                const params = source.article_id
                    ? { from_article: source.article_id }
                    : this.lineParams(source);

                fetch(this.formRoute + '&' + new URLSearchParams(params).toString(), {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                })
                    .then(response => {
                        if (!response.ok) throw response;
                        return response.text();
                    })
                    .then(html => this.mount(html))
                    .catch(() => {
                        this.loading = false;
                        this.errorMessages = ["Le formulaire n'a pas pu être chargé."];
                    });
            },
            lineParams(source) {
                const params = {};
                ['designation', 'reference', 'unit', 'description', 'amount'].forEach(key => {
                    if (source[key] !== null && source[key] !== undefined && String(source[key]).trim() !== '') {
                        params[key] = source[key];
                    }
                });

                return params;
            },
            mount(html) {
                const modal = this;

                this.formKey++;
                this.form = {
                    template: html,
                    components: {
                        ArticleFamilySelector,
                        ArticleKitComponentsTableForm,
                        ArticleKitPricingSummary,
                        ArticleProviderTableForm,
                        ArticleEcoTaxForm,
                        ArticleAttributeForm,
                        ArticleSerialNumberForm,
                        EmplacementTreeSelector,
                    },
                    // Le contenu de b-modal passe par un portail rendu plus tard : le formulaire
                    // signale lui-même qu'il est dans le DOM.
                    mounted() {
                        modal.formMounted(this.$el);
                    },
                };
            },
            formMounted(formElement) {
                this.formEl = formElement;

                // Entrée dans un champ : on enregistre ici, pas en quittant la page.
                formElement.addEventListener('submit', event => {
                    event.preventDefault();
                    this.submit();
                });
                window.initArticleForm(formElement);
                this.loading = false;
            },
            formElement() {
                return this.formEl;
            },
            submit() {
                const formElement = this.formElement();
                if (!formElement || this.saving) return;

                this.saving = true;
                this.clearErrors(formElement);

                fetch(formElement.action, {
                    method: 'POST',
                    body: new FormData(formElement),
                    headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                })
                    .then(response => response.json().then(data => ({ status: response.status, data })))
                    .then(({ status, data }) => {
                        this.saving = false;

                        if (status === 201) {
                            const callback = this.callback;
                            this.$refs.modal.hide();
                            if (callback) callback(data);
                            return;
                        }

                        if (status === 422) {
                            this.showErrors(formElement, data.errors || {});
                            return;
                        }

                        this.errorMessages = [data.message || "L'article n'a pas pu être créé."];
                    })
                    .catch(() => {
                        this.saving = false;
                        this.errorMessages = ["L'article n'a pas pu être créé."];
                    });
            },
            /** Le champ simple porte son erreur ; le récapitulatif couvre aussi les blocs des composants. */
            showErrors(formElement, errors) {
                this.errorMessages = [].concat(...Object.values(errors));

                // Une erreur dans une carte repliée : la carte s'ouvre.
                const cards = formElement.querySelectorAll('.collapse[data-error-keys]');
                Object.keys(errors).forEach(key => {
                    const field = key.split('.')[0];
                    cards.forEach(card => {
                        if (card.dataset.errorKeys.split(' ').indexOf(field) !== -1) {
                            $(card).collapse('show');
                        }
                    });
                });

                Object.keys(errors).forEach(key => {
                    const name = key.split('.').map((part, index) => index === 0 ? part : '[' + part + ']').join('');
                    const input = formElement.querySelector('[name="' + name + '"]');
                    if (!input) return;

                    input.classList.add('is-invalid');
                    // Un champ de composant (providers.0.amount) : pas de nœud ajouté dans un DOM que Vue patche.
                    if (key.indexOf('.') !== -1) return;

                    const feedback = document.createElement('div');
                    feedback.className = 'invalid-feedback js-quick-create-feedback';
                    feedback.textContent = errors[key][0];
                    input.insertAdjacentElement('afterend', feedback);
                });

                this.$refs.body.closest('.modal-body').scrollTop = 0;
            },
            clearErrors(formElement) {
                this.errorMessages = [];
                formElement.querySelectorAll('.js-quick-create-feedback').forEach(element => element.remove());
                formElement.querySelectorAll('.is-invalid').forEach(element => element.classList.remove('is-invalid'));
            },
            /** Fermée en plein enregistrement, l'article serait créé sans que la ligne le reçoive. */
            preventHideWhileSaving(event) {
                if (this.saving) {
                    event.preventDefault();
                }
            },
            reset() {
                this.form = null;
                this.formEl = null;
                this.callback = null;
                this.errorMessages = [];
                this.loading = false;
                this.saving = false;
            },
        },
    }
</script>
